// pages/AdminOrders.jsx
import React, { useState, useEffect } from 'react';

import { API_BASE } from '../config';

const statusClass = {
  pending: 'badge-pending',
  paid: 'badge-paid',
  delivered: 'badge-delivered',
  cancelled: 'badge-cancelled',
};

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const token = localStorage.getItem('token');

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API_BASE}/orders`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch');
      setOrders(await res.json());
    } catch (err) {
      setError('Could not load orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update');
      setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, status: newStatus } : o)));
    } catch (err) {
      alert('Failed to update order status. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user?.phone?.includes(searchTerm) ||
      order._id.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <p className="page muted" style={{ textAlign: 'center', marginTop: '50px' }}>Loading orders...</p>;
  if (error) return <p className="page" style={{ textAlign: 'center', color: '#ff4757', marginTop: '50px' }}>{error}</p>;

  return (
    <div className="page">
      <h1 className="display" style={{ fontSize: '26px' }}>All Orders</h1>

      <div style={{ display: 'flex', gap: '10px', margin: '16px 0', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="input"
          placeholder="Search by name, product, order ID, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: '220px' }}
        />
        <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ maxWidth: '160px' }}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {filteredOrders.length === 0 ? (
        <p className="muted">No orders match your search/filter.</p>
      ) : (
        <table className="hud-table">
          <thead>
            <tr><th>Buyer</th><th>Product</th><th>Qty</th><th>Total</th><th>Status</th><th>Action</th></tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order._id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{order.user?.name || 'Unknown'}</div>
                  <div className="muted" style={{ fontSize: '12px' }}>{order.user?.phone || order.user?.email}</div>
                  <div style={{ fontSize: '11px', color: '#6b7285', fontFamily: 'JetBrains Mono, monospace' }}>UPI-ORD-{order._id}</div>
                </td>
                <td>{order.productName}</td>
                <td>{order.quantity}</td>
                <td>₹{order.totalPrice}</td>
                <td><span className={`badge-status ${statusClass[order.status] || 'badge-pending'}`}>{order.status}</span></td>
                <td>
                  <select
                    value={order.status}
                    disabled={updatingId === order._id}
                    onChange={(e) => handleStatusChange(order._id, e.target.value)}
                    className="input"
                    style={{ padding: '6px', fontSize: '13px' }}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Mark Paid</option>
                    <option value="delivered">Mark Delivered</option>
                    <option value="cancelled">Cancel</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminOrders;
